import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';
import { useWishlist } from '@/hooks/useWishlist';
import { useProduct } from '@/hooks/useApi';
import { apiProductToProduct } from '@/services/adapters';
import ProductCard from '@/components/feature/ProductCard';
import type { Product } from '@/types';

export default function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const { data, loading } = useProduct(id || '');
  const { addToCart } = useCart();
  const { toggleWishlist, isWishlisted } = useWishlist();

  const product = data ? apiProductToProduct(data.product) : null;
  const related = data ? data.related.map(p => apiProductToProduct(p)) : [];

  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  if (loading) return (
    <main className="min-h-screen flex items-center justify-center bg-cream-50"><div className="text-center"><div className="animate-pulse w-12 h-12 bg-cream-200 rounded-full mx-auto mb-4" /><p className="text-charcoal-400 text-sm">جاري التحميل...</p></div></main>
  );

  if (!product) return (
    <main className="min-h-screen flex items-center justify-center bg-cream-50"><div className="text-center"><div className="w-20 h-20 rounded-full bg-cream-200 flex items-center justify-center mx-auto mb-4"><i className="ri-search-line text-2xl text-charcoal-400" /></div><h1 className="text-2xl font-bold text-charcoal-900 mb-4">المنتج غير موجود</h1><Link to="/shop" className="btn-primary">العودة للمتجر</Link></div></main>
  );

  const wishlisted = isWishlisted(product.id);
  const images = product.images && product.images.length > 0 ? product.images : [product.image];

  const handleAddToCart = () => {
    if (!product.inStock) return;
    addToCart(product, quantity);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleToggleWishlist = () => toggleWishlist(product.id);

  return (
    <main className="min-h-screen bg-cream-50">
      <div className="container-main py-4 md:py-8">
        {/* Breadcrumb - scrollable on mobile */}
        <div className="flex items-center gap-2 text-xs md:text-sm text-charcoal-500 mb-6 md:mb-8 overflow-x-auto whitespace-nowrap">
          <Link to="/" className="hover:text-charcoal-900">الرئيسية</Link>
          <i className="ri-arrow-left-s-line" />
          <Link to="/shop" className="hover:text-charcoal-900">المتجر</Link>
          <i className="ri-arrow-left-s-line" />
          <span className="text-charcoal-900">{product.category}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16">
          {/* Images */}
          <div>
            <div className="aspect-square rounded-2xl overflow-hidden bg-cream-100 mb-4">
              <img src={images[selectedImage] || product.image} alt={product.name} className="w-full h-full object-cover" />
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 md:gap-3 overflow-x-auto pb-2">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setSelectedImage(i)} className={`w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden border-2 flex-shrink-0 ${selectedImage === i ? 'border-charcoal-900' : 'border-cream-300'}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <p className="text-xs md:text-sm text-charcoal-400 mb-1 md:mb-2">{product.category}</p>
            <h1 className="text-xl md:text-3xl font-bold text-charcoal-900 mb-3 md:mb-4">{product.name}</h1>

            <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6">
              {product.oldPrice && <span className="text-base md:text-lg text-charcoal-400 line-through">{product.oldPrice} شيكل</span>}
              <span className="text-xl md:text-2xl font-bold text-charcoal-900">{product.price} شيكل</span>
              {product.oldPrice && <span className="px-2 py-1 bg-blush-200 text-charcoal-900 text-[10px] md:text-xs font-bold rounded-full">خصم {Math.round((1 - product.price / product.oldPrice) * 100)}%</span>}
            </div>

            <p className="text-sm text-charcoal-600 leading-relaxed mb-6 md:mb-8">{product.description}</p>

            <div className="flex flex-wrap items-center gap-3 mb-6 md:mb-8">
              <div className="flex items-center border border-cream-300 rounded-xl">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center text-charcoal-600 hover:bg-cream-100 rounded-r-xl">-</button>
                <span className="w-10 md:w-12 text-center text-sm font-medium">{product.inStock ? quantity : 0}</span>
                <button onClick={() => product.inStock && setQuantity(Math.min(product.stockQuantity ?? 99, quantity + 1))} className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center text-charcoal-600 hover:bg-cream-100 rounded-l-xl">+</button>
              </div>
              <button onClick={handleAddToCart} disabled={!product.inStock} className={`flex-1 min-w-[120px] py-2.5 md:py-3 rounded-xl text-sm font-medium transition-all ${!product.inStock ? 'bg-cream-200 text-charcoal-400 cursor-not-allowed' : added ? 'bg-green-600 text-white' : 'bg-charcoal-900 text-white hover:bg-charcoal-800'}`}>
                {!product.inStock ? 'غير متوفر' : added ? 'تمت الإضافة ✓' : 'أضف إلى السلة'}
              </button>
              <button onClick={handleToggleWishlist} className={`w-10 h-10 md:w-11 md:h-11 rounded-xl border flex items-center justify-center flex-shrink-0 transition-colors ${wishlisted ? 'bg-blush-200 border-blush-200 text-charcoal-900' : 'border-cream-300 text-charcoal-400 hover:border-charcoal-900'}`}>
                <i className={wishlisted ? 'ri-heart-fill' : 'ri-heart-line'} />
              </button>
            </div>

            {/* Details */}
            <div className="border-t border-cream-300 pt-4 md:pt-6">
              {product.sku && <div className="flex justify-between text-xs md:text-sm mb-2"><span className="text-charcoal-400">SKU</span><span className="text-charcoal-700">{product.sku}</span></div>}
              {product.badge && <div className="flex justify-between text-xs md:text-sm mb-2"><span className="text-charcoal-400">علامة</span><span className="px-2 py-0.5 bg-blush-100 text-charcoal-900 text-xs rounded-full">{product.badge}</span></div>}
              <div className="flex justify-between text-xs md:text-sm"><span className="text-charcoal-400">الحالة</span><span className={`text-sm font-medium ${product.inStock ? 'text-green-600' : 'text-red-500'}`}>{product.inStock ? 'متوفر' : 'غير متوفر'}</span></div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="mt-12 md:mt-16">
          <h2 className="text-lg md:text-xl font-bold text-charcoal-900 mb-6">الوصف</h2>
          <div className="text-sm text-charcoal-600 leading-relaxed max-w-3xl">
            <div dangerouslySetInnerHTML={{ __html: product.description || '' }} />
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-16 md:mt-20">
            <h2 className="text-lg md:text-xl font-bold text-charcoal-900 mb-6 md:mb-8 text-center">منتجات ذات صلة</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
              {related.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
